// Global Instructions Rule Applied!
//
// The prospect's role-keyed contact lists (introduced by / sourced by / lead)
// as the option shape ProspectOverviewSection and ProspectTeam consume. One
// derivation shared by every tab that hosts either section, so the Dashboard
// and the Relationship tab can never disagree about who the contacts are.
import { ProspectFields } from './fieldConfigs.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import { UNKNOWN_CONTACT } from '../../../state_manager/constants/strings.ts'
import {
  listFieldValue,
  type FieldValue,
  type FieldValueListEntry
} from '../../../core/components/InputManager/types.ts'

export interface ProspectContactOption {
  value: string
  label: string
  email: string
  data: any
}

export interface ProspectContactsByRole {
  introduced_by: ProspectContactOption[]
  sourced_by: ProspectContactOption[]
  lead: ProspectContactOption[]
}

export const EMPTY_PROSPECT_CONTACTS: ProspectContactsByRole = {
  introduced_by: [],
  sourced_by: [],
  lead: []
}

const toOption = (contact: any): ProspectContactOption => ({
  value: contact?.[UserEntryFields.contacts__id] || '',
  label:
    contact?.[UserEntryFields.prog__display_name] ||
    `${contact?.[UserEntryFields.contacts__first_name] || ''} ${contact?.[UserEntryFields.contacts__last_name] || ''}`.trim() ||
    contact?.[UserEntryFields.contacts__email] ||
    UNKNOWN_CONTACT,
  email: contact?.[UserEntryFields.contacts__email] || '',
  data: contact
})

/**
 * The option shape for one entry of a CONTACT field's value. ContactInput emits
 * `{ value, label, email, ... }` objects; the field type also admits a bare id,
 * which becomes an option labelled by that id.
 */
const toContactOption = (entry: FieldValueListEntry): ProspectContactOption =>
  typeof entry === 'object' && entry !== null
    ? {
        value: String(entry.value ?? ''),
        label: entry.label ?? '',
        email: entry.email ?? '',
        data: entry.data ?? null
      }
    : { value: String(entry), label: String(entry), email: '', data: null }

/** The contact options a CONTACT field's `onChange` value carries. */
export const contactOptionsFromFieldValue = (value: FieldValue): ProspectContactOption[] =>
  listFieldValue(value).map(toContactOption)

const listOf = (value: unknown): ProspectContactOption[] =>
  Array.isArray(value) ? value.map(toOption) : []

export const deriveProspectContacts = (prospect: any): ProspectContactsByRole => ({
  introduced_by: listOf(prospect?.[ProspectFields.contact_accounts__introduced_by]),
  sourced_by: listOf(prospect?.[ProspectFields.contact_accounts__sourced_by]),
  lead: listOf(prospect?.[ProspectFields.contact_prospects__lead])
})
